/**
 * RestTimer — countdown anchored to the server's last-set timestamp.
 *
 * Anchored (not local-state) so reload / minimize / resume / cross-channel
 * voice logs all read the same elapsed window. Math: target = restSec, used
 * = floor(now - lastSetAt), remaining = target - used.
 *
 * Stays mounted at zero with a soft "rest done" cue — does not auto-hide.
 * No audio: Safari blocks autoplay without a user gesture, and the panel is
 * always glanceable.
 */

import { useEffect, useState } from 'react';
import { TimerIcon } from '@phosphor-icons/react';

interface Props {
  /** Unix seconds of the most recent set in this session. */
  lastSetAt: number | null;
  /** Prescribed rest, in seconds, for the active exercise. */
  restSec: number | null;
}

function fmtMmSs(totalSec: number): string {
  const abs = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(abs / 60);
  const ss = abs % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export function RestTimer({ lastSetAt, restSec }: Props) {
  // Re-render once a second. We don't store the remaining value in state;
  // we recompute from props + Date.now() so cross-tab updates to lastSetAt
  // are picked up the same tick they arrive.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (lastSetAt == null || restSec == null || restSec <= 0) return;
    const t = window.setInterval(() => setTick((n) => (n + 1) % 1_000_000), 1000);
    return () => window.clearInterval(t);
  }, [lastSetAt, restSec]);

  if (lastSetAt == null || restSec == null || restSec <= 0) return null;

  const elapsed = Math.floor(Date.now() / 1000 - lastSetAt);
  const remaining = restSec - elapsed;
  const done = remaining <= 0;
  // Cap the overage we display — once you're a minute past, the exact
  // number is just noise.
  const display = done ? fmtMmSs(Math.min(-remaining, 599)) : fmtMmSs(remaining);

  return (
    <div
      className={`rest-timer${done ? ' rest-timer-done' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={done ? 'rest done' : `rest ${display} remaining`}
    >
      <TimerIcon size={14} weight="regular" aria-hidden="true" />
      <span className="rest-timer-label">{done ? 'rest done' : 'rest'}</span>
      <span className="rest-timer-value">
        {done ? `+${display}` : display}
      </span>
      <span className="rest-timer-target">/ {fmtMmSs(restSec)}</span>
    </div>
  );
}
